'use client';

import { useEffect, useRef, useState } from 'react';
import { refreshAccessToken } from '@/lib/api/client';
import type {
  SyncProgress,
  SyncStage,
  SyncErrorDetail,
} from '@/lib/onboarding/sync-types';
import { isTerminal } from '@/lib/onboarding/sync-types';

export interface UseSyncProgressOptions {
  /**
   * Backend API base. Falls back to `NEXT_PUBLIC_API_URL` / localhost.
   */
  apiBase?: string;
  /**
   * Max transient-error reconnect attempts. Default: 20.
   * Long enterprise syncs (30-60 min) pair with the BE's 90-minute SSE
   * cap; each reconnect re-reads the current Redis progress key so the
   * UI keeps following until the processor emits a terminal stage.
   */
  maxRetries?: number;
  /**
   * Milliseconds between retries (multiplied by attempt number, capped
   * at 10s per retry to avoid runaway backoff on extended syncs).
   * Default: 1000.
   */
  retryDelayMs?: number;
  /**
   * Called once when the sync reaches a terminal stage (Completed,
   * Completed With Errors, or Failed). Receives the final progress.
   */
  onTerminal?: (progress: SyncProgress) => void;
  /**
   * CANONICAL progress identity. When set, the SSE is polled with
   * `?syncConfigId=` instead of `?domainId=`. This is reliable during
   * onboarding (the worker writes `sync:cfg:<id>:progress` even before any
   * domain row exists, and the BE validates the config against the caller's
   * workspace — no fragile domain lookup). Takes precedence over `domainId`.
   */
  syncConfigId?: string | null;
}

export interface UseSyncProgressResult {
  progress: SyncProgress | null;
  stage: SyncStage;
  percentage: number;
  message: string;
  errors: SyncErrorDetail[];
  isConnecting: boolean;
  isRunning: boolean;
  isTerminal: boolean;
  isSuccess: boolean;
  isFailed: boolean;
  reconnect: () => void;
}

const DEFAULT_PROGRESS: SyncProgress = {
  stage: 'Pending',
  percentage: 0,
  message: 'Waiting to start...',
  stats: {
    totalEstimate: null,
    fetched: 0,
    staged: 0,
    analyzed: 0,
    created: 0,
    updated: 0,
    skipped: 0,
    failed: 0,
  },
  currentBatch: null,
  eta: null,
  startedAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
  errors: [],
  status: 'pending',
  totalUsers: 0,
  syncedUsers: 0,
  totalGroups: 0,
  syncedGroups: 0,
};

/**
 * Subscribe to the onboarding sync SSE stream for a given domainId.
 *
 * Native EventSource does not support custom headers, so this uses
 * fetch + ReadableStream to attach the Bearer token. Handles 401 via
 * one-shot token refresh, transient errors via bounded exponential
 * retry, and aborts cleanly on unmount.
 */
export function useSyncProgress(
  domainId: string | null | undefined,
  options: UseSyncProgressOptions = {},
): UseSyncProgressResult {
  const {
    apiBase = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:4001',
    maxRetries = 20,
    retryDelayMs = 1000,
    onTerminal,
    syncConfigId,
  } = options;

  const [progress, setProgress] = useState<SyncProgress | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [reconnectTick, setReconnectTick] = useState(0);
  const terminalFiredRef = useRef(false);
  const onTerminalRef = useRef(onTerminal);
  onTerminalRef.current = onTerminal;

  useEffect(() => {
    if (!domainId && !syncConfigId) return;
    terminalFiredRef.current = false;
    setProgress((prev) => prev ?? DEFAULT_PROGRESS);
    setIsConnecting(true);

    const controller = new AbortController();

    const run = async (attempt: number): Promise<void> => {
      const wsId =
        typeof window !== 'undefined'
          ? localStorage.getItem('hf-app:workspace-id')
          : null;

      // Prefer the config-scoped key (reliable during onboarding); fall back
      // to the legacy domain-scoped key for the established integrations page.
      const progressQuery = syncConfigId
        ? `syncConfigId=${encodeURIComponent(syncConfigId)}`
        : `domainId=${encodeURIComponent(domainId ?? '')}`;

      const buildRequest = (token: string | null): Request =>
        new Request(
          `${apiBase}/v1/onboarding/sync-progress?${progressQuery}`,
          {
            headers: {
              Authorization: token ? `Bearer ${token}` : '',
              'x-workspace-id': wsId ?? '',
              Accept: 'text/event-stream',
            },
            signal: controller.signal,
          },
        );

      try {
        let token =
          typeof window !== 'undefined'
            ? localStorage.getItem('hf-app:access-token')
            : null;
        let res = await fetch(buildRequest(token));

        if (res.status === 401) {
          const refreshed = await refreshAccessToken();
          if (refreshed) {
            token = refreshed;
            res = await fetch(buildRequest(refreshed));
          }
        }

        if (!res.ok) {
          throw new Error(`Sync progress request failed with ${res.status}`);
        }

        const reader = res.body?.getReader();
        if (!reader) throw new Error('Sync progress stream was unavailable');

        const decoder = new TextDecoder();
        let buffer = '';
        setIsConnecting(false);

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() ?? '';

          for (const line of lines) {
            if (!line.startsWith('data: ')) continue;
            try {
              const data = JSON.parse(line.slice(6)) as SyncProgress;
              setProgress(data);

              const stage = data.stage ?? 'Pending';
              const terminal =
                isTerminal(stage) ||
                data.status === 'completed' ||
                data.status === 'failed' ||
                data.status === 'no_domain' ||
                data.status === 'skipped';

              if (terminal && !terminalFiredRef.current) {
                terminalFiredRef.current = true;
                onTerminalRef.current?.(data);
                controller.abort();
                return;
              }
            } catch (parseErr) {
              if (process.env.NODE_ENV !== 'production') {
                // Surface malformed SSE payloads in dev so the server
                // can be debugged. In prod we swallow to avoid a noisy
                // console on recoverable glitches.
                console.warn(
                  '[useSyncProgress] malformed SSE data line',
                  { line, error: (parseErr as Error).message },
                );
              }
            }
          }
        }

        // Stream ended without terminal event — retry if attempts remain
        if (controller.signal.aborted || terminalFiredRef.current) return;
        if (attempt < maxRetries) {
          // Cap backoff at 10s so we don't sleep minutes between
          // reconnects during a long enterprise sync.
          const backoff = Math.min(retryDelayMs * (attempt + 1), 10_000);
          await new Promise((r) => setTimeout(r, backoff));
          if (!controller.signal.aborted) return run(attempt + 1);
        }
        setProgress((prev) =>
          prev
            ? {
                ...prev,
                stage: 'Failed',
                status: 'failed',
                message: 'Lost connection to sync progress stream',
                errors: [
                  ...prev.errors,
                  {
                    code: 'sse_disconnected',
                    message: 'Connection lost after retries',
                    retryCount: attempt,
                  },
                ],
              }
            : prev,
        );
      } catch (err) {
        const isAbort = (err as Error).name === 'AbortError';
        if (isAbort || terminalFiredRef.current) return;
        if (attempt < maxRetries) {
          // Cap backoff at 10s so we don't sleep minutes between
          // reconnects during a long enterprise sync.
          const backoff = Math.min(retryDelayMs * (attempt + 1), 10_000);
          await new Promise((r) => setTimeout(r, backoff));
          if (!controller.signal.aborted) return run(attempt + 1);
          return;
        }
        setIsConnecting(false);
        setProgress((prev) =>
          prev
            ? {
                ...prev,
                stage: 'Failed',
                status: 'failed',
                message: (err as Error).message ?? 'Sync connection failed',
                errors: [
                  ...prev.errors,
                  {
                    code: 'sse_fatal',
                    message: (err as Error).message ?? 'unknown',
                    retryCount: attempt,
                  },
                ],
              }
            : null,
        );
      }
    };

    void run(0);
    return () => controller.abort();
  }, [domainId, syncConfigId, apiBase, maxRetries, retryDelayMs, reconnectTick]);

  const stage = progress?.stage ?? 'Pending';
  const percentage = progress?.percentage ?? 0;
  const message = progress?.message ?? 'Waiting to start...';
  const errors = progress?.errors ?? [];
  // no_domain / failed are terminal failures even though their stage may still
  // read 'Pending' — surface them as failed so consumers render an escape.
  const isFailed =
    stage === 'Failed' ||
    progress?.status === 'no_domain' ||
    progress?.status === 'failed';
  const terminal = isTerminal(stage) || isFailed;
  const isSuccess = stage === 'Completed' || stage === 'Completed With Errors';
  const isRunning = !terminal && !!progress && stage !== 'Pending';

  return {
    progress,
    stage,
    percentage,
    message,
    errors,
    isConnecting,
    isRunning,
    isTerminal: terminal,
    isSuccess,
    isFailed,
    reconnect: () => {
      terminalFiredRef.current = false;
      setReconnectTick((t) => t + 1);
    },
  };
}
